import React, { useCallback, useEffect, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';

import { containerStyle, headerStyle } from './styles.ts';

import { PeopleLikeMe } from '@/components/Messanger/Chat/PeopleLikeMe/PeopleLikeMe.tsx';
import ChatItem from '@/components/Messanger/Chat/ChatItem/ChatItem.tsx';
import ChatListSkeleton from '@/components/Messanger/Chat/ChatListSkeleton/ChatListSkeleton.tsx';
import { FETCH_LIMIT, useDialogsStore } from '@/store/useDialogsStore.ts';
import UIConfig from '@/UIConfig.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';

const ROW_HEIGHT = 90;

type ChatContentProps = {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
};

const ChatContent = ({ scrollContainerRef }: ChatContentProps) => {
  const { t } = useTranslation();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const {
    fetchDialogs,
    dialogs,
    isFetching,
    allDialogsLoaded,
    resetScrollPosition
  } = useDialogsStore();

  const [initialOffset] = useState(
    () => useDialogsStore.getState().scrollPosition
  );

  const virtualizer = useVirtualizer({
    count: dialogs.data.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
    initialOffset
  });

  useEffect(() => {
    resetScrollPosition();
  }, [resetScrollPosition]);

  useEffect(() => {
    const loadInitialDialogs = async () => {
      await fetchDialogs();
      setIsInitialLoading(false);
    };

    if (isInitialLoading && !allDialogsLoaded) {
      setTimeout(() => {
        void loadInitialDialogs();
      }, 500);
    }
  }, []);

  const triggerIndex = dialogs.data.length - Math.floor(FETCH_LIMIT / 2);

  const handleLoadMore = useCallback(() => {
    if (!isFetching && !allDialogsLoaded) {
      fetchDialogs().catch((e) => {
        catchErrors(e, true);
      });
    }
  }, [isFetching, allDialogsLoaded, fetchDialogs]);

  const virtualRows = virtualizer.getVirtualItems();

  useEffect(() => {
    if (virtualRows.length === 0) return;

    const lastVisibleRow = virtualRows[virtualRows.length - 1];
    if (lastVisibleRow && lastVisibleRow.index >= triggerIndex) {
      handleLoadMore();
    }
  }, [virtualRows, triggerIndex, handleLoadMore]);

  return (
    <div style={containerStyle}>
      <h2 style={headerStyle}>{t('matches')}</h2>
      <PeopleLikeMe />
      <div
        style={{
          position: 'relative',
          paddingTop: 10,
          background: UIConfig.Colors.BackgroundWhite
        }}
      >
        {isInitialLoading && dialogs.data.length === 0 && !allDialogsLoaded ? (
          <div style={{ minHeight: '80vh' }}>
            <ChatListSkeleton />
          </div>
        ) : (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: '100%',
              position: 'relative'
            }}
          >
            {virtualRows.map((virtualRow) => {
              const dialog = dialogs.data[virtualRow.index];
              if (!dialog) return null;

              return (
                <div
                  key={dialog.contact.hid}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: ROW_HEIGHT,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <ChatItem
                    dialog={dialog}
                    scrollContainerRef={scrollContainerRef}
                    isLast={virtualRow.index === dialogs.data.length - 1}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatContent;
